import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bell, BellRinging, Receipt, Flag, CircleNotch } from "@phosphor-icons/react";

const fmtDate = (s) => (s ? String(s).replace("T", " ").slice(0, 16) : "");
const iconFor = (t) => (t === "echeance" ? Receipt : Flag);

export function NotificationBell({ collapsed, onOpenJournal }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* silencieux */ }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (open) { setLoading(true); load().finally(() => setLoading(false)); } }, [open]);

  const onClickItem = async (n) => {
    try { if (!n.read) { await api.post(`/notifications/${n.id}/read`); } } catch { /* noop */ }
    if (n.link === "journal") {
      setOpen(false);
      onOpenJournal && onOpenJournal(n.link_month || "tous");
    }
    load();
  };

  const readAll = async () => { try { await api.post("/notifications/read-all"); } catch { /* noop */ } load(); };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="notif-bell" title="Rappels"
        className={`relative w-full flex items-center rounded-md text-sm transition-colors text-muted-foreground hover:text-foreground hover:bg-muted ${collapsed ? "justify-center h-10" : "gap-2.5 px-3 py-2"}`}>
        {unread > 0 ? <BellRinging size={17} className="shrink-0 text-primary" weight="fill" /> : <Bell size={17} className="shrink-0" />}
        {!collapsed && <span className="truncate">Rappels</span>}
        {unread > 0 && (
          <span data-testid="notif-badge" className={`inline-flex items-center justify-center text-[10px] font-bold text-primary-foreground bg-primary rounded-full min-w-[18px] h-[18px] px-1 ${collapsed ? "absolute top-1 right-1" : "ml-auto"}`}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="notif-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bell size={20} weight="fill" className="text-primary" /> Rappels & échéances</DialogTitle>
            <DialogDescription>Échéances de télédéclaration (TVA / délais de paiement). Cliquez un rappel TVA pour ouvrir le journal sur la bonne période.</DialogDescription>
          </DialogHeader>

          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={readAll} data-testid="notif-read-all" disabled={!unread}>Tout marquer comme lu</Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8"><CircleNotch size={26} className="text-primary animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8" data-testid="notif-empty">Aucun rappel pour le moment.</div>
          ) : (
            <div className="space-y-2">
              {items.map((n) => {
                const Icon = iconFor(n.type);
                const clickable = n.link === "journal";
                return (
                  <button key={n.id} onClick={() => onClickItem(n)} data-testid={`notif-item-${n.id}`}
                    className={`w-full text-left rounded-lg border p-3 flex gap-3 transition-colors ${n.read ? "border-border bg-background" : "border-primary/30 bg-primary/5"} ${clickable ? "hover:border-primary hover:bg-primary/10 cursor-pointer" : "cursor-default"}`}>
                    <Icon size={18} className="text-primary shrink-0 mt-0.5" weight="fill" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-foreground flex items-center gap-2">
                        {n.title}
                        {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{n.message}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">{fmtDate(n.ts)}{clickable ? " · Ouvrir le journal →" : ""}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
